import React, { useState } from "react";
import GenericSupplyTable from "./GenericSupplyTable";
import LinkHardwareModal from "./LinkHardwareModal";
import NeedsOrderingPanel from "./NeedsOrderingPanel";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const TABS = [
  { key: "hardware", label: "Hardware" },
  { key: "shipping", label: "Shipping Supplies" },
];

export default function StoreSuppliesPage({ storeName, store, hardware, setHardware, shippingSupplies, setShippingSupplies }) {
  const [tab, setTab] = useState("hardware");
  const [linking, setLinking] = useState(null);

  const apiCrud = (base, loc, { surfaceDeleteErrors = false } = {}) => ({
    onAdd: (data, cb) => {
      fetch(`${API_URL}${base}?location=${loc}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) })
        .then((r) => r.json()).then((created) => { if (created.id) cb(created); }).catch(() => {});
    },
    onEdit: (id, data) => {
      fetch(`${API_URL}${base}/${id}?location=${loc}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }).catch(() => {});
    },
    // Hardware delete can legitimately fail (409 when products still use it),
    // so that tab returns the promise and lets the table put the row back.
    onDelete: surfaceDeleteErrors
      ? (id) =>
          fetch(`${API_URL}${base}/${id}?location=${loc}`, { method: "DELETE" }).then(async (r) => {
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Could not delete that.");
          })
      : (id) => {
          fetch(`${API_URL}${base}/${id}?location=${loc}`, { method: "DELETE" }).catch(() => {});
        },
  });

  // Quantity is eaches. Packs are shown beside it because Daniel buys screws in
  // bags of 100 but uses them one at a time — the count stays whole either way.
  const qtyCell = (item) => {
    const qty = Number(item.quantity) || 0;
    const pack = Number(item.packQty) || 1;
    return (
      <span>
        {qty}
        {pack > 1 && (
          <span style={{ color: "rgba(10,10,15,0.45)", fontSize: 12, marginLeft: 6 }}>
            {(qty / pack).toFixed(1)} pk
          </span>
        )}
      </span>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Hardware & Supplies</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>Manage hardware inventory and shipping supplies</p>
      </div>
      <div style={{ marginBottom: 24 }}>
        <nav style={S.nav}>
          {TABS.map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)} style={{ ...S.navBtn, ...(tab === key ? S.navBtnActive : {}) }}>{label}</button>
          ))}
        </nav>
      </div>
      {tab === "hardware" && (
        <>
        <NeedsOrderingPanel hardware={hardware} location={store} />
        <GenericSupplyTable
          title="Hardware" items={hardware} setItems={setHardware}
          {...apiCrud("/api/hardware", store, { surfaceDeleteErrors: true })}
          rowStyle={(item) => (item.isLow ? { background: "rgba(194,65,15,0.10)" } : {})}
          rowActions={(item) => (
            <button
              onClick={() => setLinking(item)}
              style={{ ...S.iconBtn, marginRight: 6, width: "auto", padding: "0 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}
              title="Choose which products use this"
            >
              {item.usedBy ? `${item.usedBy} USED BY` : "LINK"}
            </button>
          )}
          columns={[
            { key: "name", label: "Item Name", width: "20%", placeholder: "e.g. M5 x 12 screw" },
            { key: "category", label: "Category", width: "12%", placeholder: "e.g. Fasteners" },
            { key: "sku", label: "SKU / Part #", width: "12%", placeholder: "Part #" },
            {
              key: "quantity", label: "On hand", width: "11%", placeholder: "0", type: "number", align: "center",
              render: qtyCell,
            },
            { key: "packQty", label: "Pack of", width: "8%", placeholder: "100", type: "number", align: "center" },
            {
              key: "lowAt", label: "Low at", width: "8%", placeholder: "150", type: "number", align: "center",
              render: (item) => (item.lowAt == null ? "—" : item.lowAt),
            },
            {
              key: "buyUrl", label: "Reorder", width: "10%", placeholder: "https://amazon…",
              render: (item) =>
                item.buyUrl ? (
                  <a href={item.buyUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#0033FF", fontSize: 12 }}>Buy</a>
                ) : "—",
            },
            { key: "price", label: "Pack price", width: "10%", placeholder: "0.00", type: "number", align: "right", isCurrency: true },
          ]}
        />
        </>
      )}
      {linking && (
        <LinkHardwareModal
          hardware={linking}
          store={store}
          storeName={storeName}
          onClose={() => setLinking(null)}
          onSaved={(count) => setHardware(hardware.map((h) => (h.id === linking.id ? { ...h, usedBy: count } : h)))}
        />
      )}
      {tab === "shipping" && (
        <GenericSupplyTable
          title="Shipping Supplies" items={shippingSupplies} setItems={setShippingSupplies}
          {...apiCrud("/api/shipping-supplies", store)}
          columns={[
            { key: "name", label: "Item Name", width: "20%", placeholder: "e.g. Bubble Mailers" },
            { key: "size", label: "Size", width: "12%", placeholder: '8x12"' },
            { key: "brand", label: "Brand", width: "14%", placeholder: "e.g. Uline" },
            { key: "sku", label: "SKU", width: "14%", placeholder: "SKU #" },
            { key: "quantity", label: "Qty", width: "8%", placeholder: "0", type: "number", align: "center" },
            { key: "price", label: "Price", width: "10%", placeholder: "0.00", type: "number", align: "right", isCurrency: true },
          ]}
        />
      )}
    </div>
  );
}
